import { makeAutoObservable, runInAction } from 'mobx';
import { makePersistable } from 'mobx-persist-store';
import { IUser, IUserResponse, IUserRoles, IWorker } from 'models';
import { IPrices } from 'models/prices.model';
import { secureLs } from 'utils';

class User {
	data: IUser | null = null;
	prices: IPrices | null = null;
	workers: IWorker[] = [];
	hydrated = false;

	constructor() {
		makeAutoObservable(this);
		makePersistable(this, { name: 'user', properties: ['data', 'prices', 'workers'], storage: window.localStorage }).then(() => {
			runInAction(() => { this.hydrated = true; });
		});
	}

	setUser(data: IUserResponse) {
		this.data = data.user;
		this.prices = data.prices;
		this.workers = data.workers;
		secureLs.set('token', data.token);
	}

	logout() {
		localStorage.clear();
		this.data = null;
	}

	checkUserId(arr: number[]) {
		return arr.includes(Number(this.data?.id));
	}

	updatePrice(key: keyof IPrices, value: number) {
		if(this.prices?.[key]) {
			this.prices[key] = value;
		}
	}

	updateUser(data: IWorker) {
		const index = this.workers.findIndex(user => user.id === data.id);
		if(index !== -1) {
			this.workers[index] = data;
		}
	}

	addUser(data: IWorker) {
		this.workers.push(data);
	}

	removeUser(id: number) {
		this.workers = this.workers.filter(user => user.id !== id);
	}

	calculateHourlyRate(movers: number, isWeekend: boolean, isCash: boolean) {
		if(this.prices) {	
			const { price, moverPrice, weekendPrice, nonCashPrice } = this.prices;
			return price + (movers * moverPrice) + (isWeekend ? weekendPrice : 0) + (isCash ? 0 : nonCashPrice);
		} else {
			return 0;
		}
	}

	getUser(id?: number) {
		return this.workers.find(user => user.id === id);
	}

	get isAdmin() {
		return this.data?.role.includes(IUserRoles.ADMIN);
	}

	get isManager() {
		return this.data?.role.includes(IUserRoles.MANAGER);
	}

	get isForeman() {
		return this.data?.role.includes(IUserRoles.FOREMAN);
	}

	get isStaff() {
		return this.isManager || this.isAdmin;
	}

	get isAuth() {
		return !!this.data;
	}

	get role() {
		return this.data?.role?.[0];
	}

	get helpers() {
		return this.workers.filter(el => el.roles.includes(IUserRoles.HELPER));
	}

	get foremans() {
		return this.workers.filter(el => el.roles.includes(IUserRoles.FOREMAN));
	}

	get admins() {
		return this.workers.filter(el => el.roles.includes(IUserRoles.ADMIN));
	}

	get managers() {
		return this.workers.filter(el => el.roles.includes(IUserRoles.MANAGER));
	}

	get helpersForemans() {
		return [...this.helpers, ...this.foremans];
	}

	get staff() {
		return [...this.admins, ...this.managers];
	}
}

export const userStore = new User();